import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { apiRequest } from "../context/AuthContext";
import { Wand2, Copy, ArrowLeft, ChevronDown, Trash2, X, BookMarked, Film, Mail, ExternalLink, CheckCircle, MessageSquare, AlertTriangle, CalendarClock, Sparkles } from "lucide-react";

/**
 * Pure helper — normalise the `division` field from the DB.
 * Handles every variant we have in live data:
 *   D1 / Division I  → isD1
 *   DII / Division II → isDII
 *   DIII / Division III → isDIII
 *   NAIA              → isNAIA
 *   JUCO (NJCAA-affiliated) → isJUCO
 */
function getDivisionMeta(college) {
  const empty = { isD1: false, isNAIA: false, isJUCO: false, isDII: false, isDIII: false, showToggle: false, autoOn: false };
  if (!college) return empty;
  const div = (college.division || "").trim().toLowerCase();
  const isD1   = div === "division i"   || div === "d1"   || div === "d-i";
  const isNAIA = div === "naia"         || div.includes("naia");
  // DB stores NJCAA community-college programmes as "JUCO"
  const isJUCO = div === "juco"         || div.includes("juco")  || div.includes("njcaa");
  // Handle both "Division II" (most common) and short-form "DII" / "D2"
  const isDII  = div === "division ii"  || div === "dii"  || div === "d2";
  const isDIII = div === "division iii" || div === "diii" || div === "d3";
  const showToggle = !isD1 && (isNAIA || isJUCO || isDII || isDIII);
  const autoOn = isNAIA || isJUCO || ((isDII || isDIII) && !!college.foreign_friendly);
  return { isD1, isNAIA, isJUCO, isDII, isDIII, showToggle, autoOn };
}

/**
 * Returns visibility + urgency state for the Late Enquiry (Post-Start) button.
 * Window: September 1 – November 30 of the current year.
 * Urgency: final 2 weeks (Nov 16–30) switch to countdown copy.
 */
function getLateEnquiryMeta() {
  const today = new Date();
  const yr = today.getFullYear();
  const windowStart  = new Date(yr, 8,  1);             // Sept 1  (month 0-indexed)
  const windowEnd    = new Date(yr, 10, 30, 23, 59, 59); // Nov 30, end of day
  const urgencyStart = new Date(yr, 10, 16);             // Nov 16
  const inWindow = today >= windowStart && today <= windowEnd;
  if (!inWindow) return { inWindow: false, isUrgent: false, daysLeft: null };
  const isUrgent = today >= urgencyStart;
  let daysLeft = null;
  if (isUrgent) {
    const msLeft = windowEnd.getTime() - today.getTime();
    daysLeft = Math.max(1, Math.ceil(msLeft / (1000 * 60 * 60 * 24)));
  }
  return { inWindow, isUrgent, daysLeft };
}

// Pure helper — build rich stats string from a profile object.
// Extracted here (not inside the component) so it can be called both
// at mount AND inside generateDraft with a freshly-fetched profile.
function buildStatsFromProfile(p) {
  const statContexts = [
    { label: "School",   keys: ["college_ppg","college_apg","college_rpg","college_spg","college_fg_percent","college_three_pt_percent"] },
    { label: "Club",     keys: ["academy_ppg","academy_apg","academy_rpg","academy_spg","academy_fg_percent","academy_three_pt_percent"] },
    { label: "National", keys: ["country_ppg","country_apg","country_rpg","country_spg","country_fg_percent","country_three_pt_percent"] },
  ];
  const statLabels = ["PPG","APG","RPG","SPG","FG%","3PT%"];
  const lines = [];
  statContexts.forEach(ctx => {
    const parts = ctx.keys.map((k, i) => p[k] ? `${p[k]} ${statLabels[i]}` : null).filter(Boolean);
    if (parts.length > 0) lines.push(`${ctx.label}: ${parts.join(", ")}`);
  });
  if (lines.length === 0 && p.ppg) {
    const legacy = [`${p.ppg} PPG`, p.apg && `${p.apg} APG`, p.rpg && `${p.rpg} RPG`].filter(Boolean);
    lines.push(legacy.join(", "));
  }
  if (p.current_team) lines.push(`National Team: ${p.current_team}`);
  if (p.club_team) lines.push(`Club: ${p.club_team}`);
  return lines.join("\n");
}

// Known placeholder tokens that MUST NOT appear in a sent message.
// These strings are the exact fallback values used when player context fails to load.
const SEND_GUARD_PATTERNS = [/\bPlayer\b/];

export default function ComposePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const preloaded = location.state;

  const [collegeContext, setCollegeContext] = useState(null);
  const [playerProfile, setPlayerProfile] = useState({});
  const [colleges, setColleges] = useState([]);
  const [selectedCollege, setSelectedCollege] = useState(preloaded?.college || null);
  const [selectedCoach, setSelectedCoach] = useState(preloaded?.coach || null);
  const [messageType, setMessageType] = useState(preloaded?.messageType || "initial_outreach");
  const [position, setPosition] = useState("point guard");
  const [secondaryPosition, setSecondaryPosition] = useState("");
  const [stats, setStats] = useState("");
  const [subject, setSubject] = useState("");
  const [draft, setDraft] = useState("");
  const [generating, setGenerating] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mailtoDraftCopied, setMailtoDraftCopied] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState("");
  // Hard-stop before send: set when placeholder tokens detected in draft/subject
  const [sendError, setSendError] = useState("");
  // January / spring enrollment context toggle
  const [useJanContext, setUseJanContext] = useState(false);
  // Global admin kill switch — true until profile fetch says otherwise
  const [janEnrollmentEnabled, setJanEnrollmentEnabled] = useState(true);
  // Late-opening roster context — auto-on, driven by selectedCollege.late_opening_status
  const [useLateOpeningContext, setUseLateOpeningContext] = useState(false);

  // Template library state
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateSaved, setTemplateSaved] = useState(false);

  // Prior outreach detection
  const [alreadyContacted, setAlreadyContacted] = useState(false);

  // When college changes, auto-set the correct next message type based on email history
  useEffect(() => {
    if (!selectedCollege?.id) { setAlreadyContacted(false); setCollegeContext(null); return; }
    apiRequest("get", `/emails?college_id=${selectedCollege.id}`)
      .then(r => {
        const sent = (r.data || []).filter(e => e.direction === "sent").map(e => e.message_type);
        const has = (type) => sent.includes(type);
        if (has("second_follow_up") || has("follow_up")) {
          setAlreadyContacted(true);
          setMessageType("second_follow_up");
        } else if (has("initial_outreach")) {
          setAlreadyContacted(true);
          setMessageType("follow_up");
        } else {
          setAlreadyContacted(false);
        }
      })
      .catch(() => {});
    // Fetch reply context for context-aware drafts
    apiRequest("get", `/emails/college-context/${selectedCollege.id}`)
      .then(r => setCollegeContext(r.data))
      .catch(() => {});
  }, [selectedCollege?.id]);

  // Auto-set January context toggle when college changes.
  // Delegates entirely to getDivisionMeta() — no duplicated string logic here.
  // Also re-evaluates when the global kill switch changes.
  useEffect(() => {
    if (!selectedCollege || !janEnrollmentEnabled) { setUseJanContext(false); return; }
    const { showToggle, autoOn } = getDivisionMeta(selectedCollege);
    if (!showToggle) { setUseJanContext(false); return; }
    if (autoOn) { setUseJanContext(true); return; }
    setUseJanContext(false);
  }, [selectedCollege?.id, selectedCollege?.foreign_friendly, janEnrollmentEnabled]);

  // Auto-set late-opening context when the selected college has a live roster signal.
  useEffect(() => {
    const s = selectedCollege?.late_opening_status;
    setUseLateOpeningContext(s === "likely_open" || s === "confirmed_open");
  }, [selectedCollege?.id, selectedCollege?.late_opening_status]);

  useEffect(() => {
    apiRequest("get", "/colleges").then(r => setColleges(r.data)).catch(() => {});
    apiRequest("get", "/profile").then(r => {
      const p = r.data || {};
      setPlayerProfile(p);
      if (p.primary_position) setPosition(p.primary_position.toLowerCase());
      else if (p.position) setPosition(p.position.toLowerCase());
      if (p.secondary_position && p.secondary_position !== "None") setSecondaryPosition(p.secondary_position.toLowerCase());
      setStats(buildStatsFromProfile(p));
      // Global kill switch — default true if the field is absent (e.g. unauthenticated fallback)
      setJanEnrollmentEnabled(p.january_enrollment_enabled !== false);
      setProfileLoaded(true);
    }).catch(() => { setProfileLoaded(true); });
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const r = await apiRequest("get", "/templates");
      setTemplates(r.data);
    } catch {}
  };

  const generateDraft = async () => {
    if (!selectedCollege) { setError("Please select a college first."); return; }
    setError("");
    setSendError("");
    setGenerating(true);
    setShowSaveTemplate(false);

    // ── Step 1: Re-fetch player profile fresh every time. ─────────────────────
    // Never reuse state from component mount — context may be stale if the user
    // has stayed on this page across multiple sessions or switched colleges.
    let freshProfile = {};
    try {
      const profileRes = await apiRequest("get", "/profile");
      freshProfile = profileRes.data || {};
      // Update all derived state so the UI reflects what the AI will use
      setPlayerProfile(freshProfile);
      setStats(buildStatsFromProfile(freshProfile));
      if (freshProfile.primary_position) setPosition(freshProfile.primary_position.toLowerCase());
      else if (freshProfile.position) setPosition(freshProfile.position.toLowerCase());
      if (freshProfile.secondary_position && freshProfile.secondary_position !== "None") {
        setSecondaryPosition(freshProfile.secondary_position.toLowerCase());
      }
    } catch {
      // Profile fetch failed — fall back to last-known state, but warn.
      freshProfile = playerProfile;
    }

    // ── Step 2: Hard gate — name must be resolved before sending to AI. ───────
    // No "Player" fallback. If name is missing, surface an actionable error.
    const playerName = freshProfile.full_name;
    if (!playerName) {
      setError("Your full name is not set in your profile — please update your profile and try again.");
      setGenerating(false);
      return;
    }

    // ── Step 3: Build context from fresh profile data (not stale state). ──────
    const freshPosition = freshProfile.primary_position?.toLowerCase() || freshProfile.position?.toLowerCase() || position;
    const freshSecondary = (freshProfile.secondary_position && freshProfile.secondary_position !== "None")
      ? freshProfile.secondary_position.toLowerCase()
      : (secondaryPosition || "");
    const freshStats = buildStatsFromProfile(freshProfile);

    try {
      const { data } = await apiRequest("post", "/ai/draft-message", {
        college_name: selectedCollege.name,
        coach_name: selectedCoach?.name || "Coach",
        division: selectedCollege.division || "Division I",
        user_name: playerName,                          // no "Player" fallback
        user_position: freshPosition,
        user_secondary_position: freshSecondary,
        user_stats: freshStats,
        user_email: freshProfile.email || "",
        user_phone: freshProfile.phone || "",
        highlight_tape_url: freshProfile.highlight_tape_url || "",
        message_type: messageType,
        college_reply_outcome: ["initial_outreach","follow_up"].includes(messageType) ? "" : (collegeContext?.reply_outcome || ""),
        college_reply_body:    ["initial_outreach","follow_up"].includes(messageType) ? "" : (collegeContext?.latest_reply?.body || ""),
        ...(useLateOpeningContext
          ? { opportunity_context: "late_opening" }
          : messageType === "late_enquiry"
            ? { opportunity_context: "late_enquiry" }
            : useJanContext
              ? { opportunity_context: "january_enrollment" }
              : {}),
        // Pass the player's GPA so the AI can add a merit-aid line for D2/D3/NAIA.
        // Only populated when the player has saved a GPA equivalent to their profile.
        ...(freshProfile.gpa_equivalent ? { user_gpa_equivalent: freshProfile.gpa_equivalent } : {}),
        // Extended profile fields for the structured initial-outreach template
        user_height: freshProfile.height_ft || "",
        user_nationality: freshProfile.nationality || "",
        user_current_school: freshProfile.current_school || "",
        user_intended_major: freshProfile.intended_major || "",
        user_target_start_year: freshProfile.target_start_year || "",
      });
      setDraft(data.draft);
      // Update subject only if blank or if it still holds a stale placeholder.
      // Prefer the AI-returned subject (data.subject) when it exists — the backend
      // now generates a structured headline for initial_outreach. Fall back to the
      // local subject map for other message types or when the AI returns no subject.
      const subjectNeedsUpdate = !subject || SEND_GUARD_PATTERNS.some(p => p.test(subject));
      if (subjectNeedsUpdate) {
        if (data.subject) {
          setSubject(data.subject);
        } else {
          const subjectMap = {
            initial_outreach:  `Basketball Scholarship Inquiry — ${playerName}`,
            follow_up:         `Follow-Up: Basketball Scholarship Inquiry — ${selectedCollege.name}`,
            second_follow_up:  `Second Follow-Up: Basketball Scholarship — ${playerName}`,
            reply_to_interest: `Re: Your Interest — ${playerName} | Basketball`,
            reply_to_offer:    `Re: Scholarship Offer — ${playerName} | ${selectedCollege.name}`,
            after_call:        `Following Our Call — ${playerName} | ${selectedCollege.name} Basketball`,
            after_visit:       `Following My Campus Visit — ${playerName} | ${selectedCollege.name}`,
            thank_you:         `Thank You — ${playerName} | ${selectedCollege.name}`,
            no_interest:       `Thank You for Your Time — ${playerName} | ${selectedCollege.name}`,
            late_enquiry:      `Late Roster Enquiry — ${playerName} | ${selectedCollege?.name || ""}`,
          };
          setSubject(subjectMap[messageType] || "Basketball Scholarship Inquiry");
        }
      }
    } catch {
      setError("AI generation failed. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const copyDraft = () => {
    navigator.clipboard.writeText(draft);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Send-guard ────────────────────────────────────────────────────────────
  // Hard-stop: block Gmail open if any known placeholder token survives in the draft.
  // Returns an error string, or null if clear.
  const validateBeforeSend = () => {
    // Edge-case safety: if the player's own registered name legitimately contains the
    // word "Player" (e.g. surname "Player"), skip the check — it's not a placeholder.
    const ownNameHasPlayer = /\bPlayer\b/i.test(playerProfile.full_name || "");
    if (!ownNameHasPlayer) {
      for (const pattern of SEND_GUARD_PATTERNS) {
        if (pattern.test(draft) || pattern.test(subject)) {
          return 'This draft still contains the placeholder "Player" — your name wasn\'t loaded correctly. Regenerate the draft before sending.';
        }
      }
    }
    return null;
  };

  const openInGmail = () => {
    if (!draft || !selectedCollege) return;
    const coachEmail = (selectedCoach?.email || "").trim();
    // Hard gate: never open Gmail without a recipient address
    if (!coachEmail) return;

    // Hard gate: block send if placeholder tokens detected
    const placeholderError = validateBeforeSend();
    if (placeholderError) { setSendError(placeholderError); return; }
    setSendError("");
    const sendingEmail = playerProfile.email || "";
    const authParam = sendingEmail ? `&authuser=${encodeURIComponent(sendingEmail)}` : "";
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1${authParam}&to=${encodeURIComponent(coachEmail)}&su=${encodeURIComponent(subject || "Basketball Scholarship Inquiry")}&body=${encodeURIComponent(draft)}`;
    const link = document.createElement("a");
    link.href   = gmailUrl;
    link.target = "_blank";
    link.rel    = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setAwaitingConfirmation(true);
    setConfirmed(false);
  };

  // Non-Gmail fallback: player's own email domain isn't Gmail/Google Workspace,
  // so a mail.google.com compose link would be the wrong account. Open the
  // system default mail client via a plain mailto: link instead.
  const GMAIL_DOMAINS = ["gmail.com", "googlemail.com"];
  const getPlayerEmailDomain = () =>
    (playerProfile.email || "").trim().toLowerCase().split("@")[1] || "";
  const isPlayerEmailGmail = () => GMAIL_DOMAINS.includes(getPlayerEmailDomain());

  const openMailtoFallback = () => {
    if (!draft || !selectedCollege) return;
    const coachEmail = (selectedCoach?.email || "").trim();
    // Hard gate: never open a mail client without a recipient address
    if (!coachEmail) return;

    // Hard gate: block send if placeholder tokens detected
    const placeholderError = validateBeforeSend();
    if (placeholderError) { setSendError(placeholderError); return; }
    setSendError("");
    const mailtoUrl = `mailto:${coachEmail}?subject=${encodeURIComponent(subject || "Basketball Scholarship Inquiry")}&body=${encodeURIComponent(draft)}`;
    window.location.href = mailtoUrl;
    setAwaitingConfirmation(true);
    setConfirmed(false);
  };

  const copyDraftForMailto = () => {
    // Reuse the same coach-email resolution used by openMailtoFallback/openInGmail
    // — don't re-derive it separately.
    const coachEmail = (selectedCoach?.email || "").trim();
    const formatted = `To: ${coachEmail}\nSubject: ${subject || "Basketball Scholarship Inquiry"}\n\n${draft}\n\n`;
    navigator.clipboard.writeText(formatted);
    setMailtoDraftCopied(true);
    setTimeout(() => setMailtoDraftCopied(false), 2000);
  };

  const confirmEmailSent = async () => {
    if (!selectedCollege) return;
    setConfirming(true);
    try {
      await apiRequest("post", "/emails", {
        college_id: selectedCollege.id,
        direction: "sent",
        subject: subject || "Basketball Scholarship Inquiry",
        body: draft,
        coach_name: selectedCoach?.name || "",
        coach_email: selectedCoach?.email || "",
        message_type: messageType,
      });
      try {
        await apiRequest("post", "/my-colleges", { college_id: selectedCollege.id, notes: "" });
      } catch {} // 400 = already tracked
      const followUpDate = new Date();
      followUpDate.setDate(followUpDate.getDate() + 7);
      await apiRequest("patch", `/my-colleges/${selectedCollege.id}/status`, {
        status: "contacted",
        follow_up_date: followUpDate.toISOString().split("T")[0],
        message_context: useLateOpeningContext ? "late_opening" : null,
      });
      setAwaitingConfirmation(false);
      setConfirmed(true);
      setTimeout(() => setConfirmed(false), 6000);
    } catch (err) {
      console.error("Failed to log email:", err);
    } finally {
      setConfirming(false);
    }
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    if (!templateName.trim() || !draft) return;
    setSavingTemplate(true);
    try {
      const res = await apiRequest("post", "/templates", {
        name: templateName.trim(),
        subject: subject || "",
        body: draft,
        message_type: messageType,
      });
      setTemplates(prev => [res.data, ...prev]);
      setTemplateName("");
      setShowSaveTemplate(false);
      setTemplateSaved(true);
      setTimeout(() => setTemplateSaved(false), 2500);
    } catch {}
    setSavingTemplate(false);
  };

  const loadTemplate = (tpl) => {
    setDraft(tpl.body);
    if (tpl.subject) setSubject(tpl.subject);
    setMessageType(tpl.message_type || "initial_outreach");
    setShowTemplates(false);
  };

  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    try {
      await apiRequest("delete", `/templates/${id}`);
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch {}
  };

  const messageTypes = [
    { value: "initial_outreach",  label: "Initial Outreach"   },
    { value: "follow_up",         label: "Follow-Up"          },
    { value: "second_follow_up",  label: "2nd Follow Up"      },
    { value: "reply_to_interest", label: "Reply to Interest"  },
    { value: "reply_to_offer",    label: "Reply to Offer"     },
    { value: "after_call",        label: "After Call"         },
    { value: "after_visit",       label: "After Visit"        },
    { value: "no_interest",       label: "No Interest"        },
  ];

  return (
    <div style={{ fontFamily: "Manrope, sans-serif" }}>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm mb-5 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="mb-6">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-orange-600">AI Message Composer</span>
        <h1 className="text-3xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
          Draft Your Email
        </h1>
        <p className="text-slate-500 mt-1">Let AI help you write a compelling message to coaches</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Settings Panel */}
        <div className="space-y-4">

          {/* Template Library Panel */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <button
              data-testid="templates-toggle-btn"
              onClick={() => setShowTemplates(!showTemplates)}
              className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <BookMarked className="w-4 h-4 text-slate-500" />
                <span className="font-bold text-sm text-slate-700 uppercase tracking-wider" style={{ fontFamily: "Barlow Condensed, sans-serif", letterSpacing: "0.05em" }}>
                  My Templates
                </span>
                {templates.length > 0 && (
                  <span className="bg-orange-100 text-orange-600 text-xs font-bold px-2 py-0.5 rounded-full">
                    {templates.length}
                  </span>
                )}
              </div>
              <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showTemplates ? "rotate-180" : ""}`} />
            </button>

            {showTemplates && (
              <div className="border-t border-slate-100 p-4">
                {templates.length === 0 ? (
                  <p className="text-slate-400 text-sm text-center py-3">
                    No saved templates yet. Generate a draft and save it as a template.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {templates.map(tpl => (
                      <div
                        key={tpl.id}
                        data-testid={`template-item-${tpl.id}`}
                        onClick={() => loadTemplate(tpl)}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-orange-50 transition-colors group"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800 truncate">{tpl.name}</p>
                          <p className="text-xs text-slate-400 mt-0.5 truncate">{tpl.subject || "No subject"}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          <span className="text-xs bg-orange-100 text-orange-600 font-bold px-2 py-0.5 rounded-full uppercase">
                            {tpl.message_type?.replace("_", " ")}
                          </span>
                          <button
                            data-testid={`delete-template-${tpl.id}`}
                            onClick={(e) => deleteTemplate(tpl.id, e)}
                            className="text-slate-300 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h2 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>Message Settings</h2>

            {/* College selector */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Target College *</label>
              <div className="relative">
                <select
                  data-testid="compose-college-select"
                  value={selectedCollege?.id || ""}
                  onChange={e => {
                    const c = colleges.find(c => c.id === e.target.value);
                    setSelectedCollege(c || null);
                    setSelectedCoach(null);
                  }}
                  className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 outline-none appearance-none bg-white pr-8 text-slate-900"
                >
                  <option value="">Select a college...</option>
                  {colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
                <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Coach selector */}
            {selectedCollege?.coaches?.length > 0 && (
              <div className="mb-4">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Coach</label>
                <div className="relative">
                  <select
                    data-testid="compose-coach-select"
                    value={selectedCoach?.name || ""}
                    onChange={e => setSelectedCoach(selectedCollege.coaches.find(c => c.name === e.target.value) || null)}
                    className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 outline-none appearance-none bg-white pr-8 text-slate-900"
                  >
                    <option value="">Select a coach...</option>
                    {selectedCollege.coaches.map(c => <option key={c.name} value={c.name}>{c.name} - {c.title}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Prior outreach warning */}
            {alreadyContacted && (
              <div className="mb-4 flex items-start gap-2.5 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3" data-testid="already-contacted-warning">
                <span className="text-amber-500 mt-0.5 flex-shrink-0">⚠</span>
                <div>
                  <p className="text-sm font-bold text-amber-800">Initial outreach already sent to {selectedCollege?.name}</p>
                  <p className="text-xs text-amber-700 mt-0.5">Switched to <strong>Follow-Up</strong> automatically. You can change this below if needed.</p>
                </div>
              </div>
            )}

            {/* ── Late roster opening banner ────────────────────────────────
                Auto-activates when selectedCollege.late_opening_status is
                'likely_open' or 'confirmed_open'. Not toggleable — it's a
                factual signal about the college, not a user preference.        */}
            {useLateOpeningContext && selectedCollege && (() => {
              const status = selectedCollege.late_opening_status;
              const confidence = selectedCollege.late_opening_confidence;
              const verifiedAt = selectedCollege.late_opening_verified_at;
              const daysAgo = verifiedAt
                ? Math.floor((Date.now() - new Date(verifiedAt).getTime()) / 86400000)
                : null;
              const statusLabel = status === "confirmed_open" ? "CONFIRMED OPEN" : "LIKELY OPEN";
              const statusColour = status === "confirmed_open"
                ? "border-2 border-emerald-400 shadow-sm shadow-emerald-100"
                : "border-2 border-amber-400 shadow-sm shadow-amber-100";
              const headerBg = status === "confirmed_open" ? "bg-emerald-500" : "bg-amber-500";
              const bodyBg   = status === "confirmed_open" ? "bg-emerald-50"  : "bg-amber-50";
              const textBody = status === "confirmed_open" ? "text-emerald-800" : "text-amber-800";
              return (
                <div
                  className={`mb-4 rounded-xl overflow-hidden ${statusColour}`}
                  data-testid="late-opening-banner"
                >
                  {/* Header */}
                  <div className={`flex items-center justify-between px-4 py-2.5 ${headerBg}`}>
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-3.5 h-3.5 text-white flex-shrink-0" />
                      <span className="text-xs font-extrabold uppercase tracking-widest text-white">
                        Late Roster Opening
                      </span>
                      {confidence && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white/25 text-white uppercase">
                          {confidence} confidence
                        </span>
                      )}
                    </div>
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-white">
                      {statusLabel}
                    </span>
                  </div>
                  {/* Body */}
                  <div className={`px-4 py-3 flex items-start gap-3 ${bodyBg}`}>
                    <Sparkles className={`w-4 h-4 flex-shrink-0 mt-0.5 ${status === "confirmed_open" ? "text-emerald-500" : "text-amber-500"}`} />
                    <div>
                      <p className={`text-xs leading-relaxed ${textBody}`}>
                        <strong>Active —</strong> AI will lead with immediate roster availability
                        and include your GPA prominently.
                        {daysAgo !== null && (
                          <span className="ml-1 opacity-75">
                            Verified {daysAgo === 0 ? "today" : `${daysAgo} day${daysAgo !== 1 ? "s" : ""} ago`}.
                          </span>
                        )}
                      </p>
                      <p className={`text-xs mt-1.5 ${textBody} opacity-80 leading-relaxed`}>
                        This college may have an unexpected roster vacancy for the current season —
                        verified by a coach or staff source ({status === "confirmed_open" ? "confirmed" : "likely"}).
                        Your draft will lead with availability rather than a standard scholarship inquiry.
                      </p>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── January / spring enrollment context card ──────────────────────
                Placed BEFORE Message Type so it informs the framing choice,
                not as an afterthought below it.
                Visibility rules handled by getDivisionMeta(); the global admin
                kill switch (janEnrollmentEnabled) hides it unconditionally.  */}
            {selectedCollege && janEnrollmentEnabled && (() => {
              const { showToggle, isNAIA, isJUCO, isDII, isDIII, autoOn } = getDivisionMeta(selectedCollege);
              if (!showToggle) return null;
              const badge = isNAIA ? "NAIA" : isJUCO ? "NJCAA / JUCO"
                : ((isDII || isDIII) && selectedCollege.foreign_friendly) ? "Recommended" : "Available";
              const badgeStyle = (isNAIA || isJUCO || ((isDII || isDIII) && selectedCollege.foreign_friendly))
                ? "bg-blue-100 text-blue-700 border border-blue-200"
                : "bg-slate-100 text-slate-500 border border-slate-200";
              return (
                <div className="mb-4" data-testid="jan-context-card">
                  <button
                    onClick={() => setUseJanContext(v => !v)}
                    data-testid="jan-context-toggle"
                    className={`w-full text-left rounded-xl transition-all duration-200 overflow-hidden ${
                      useJanContext
                        ? "border-2 border-blue-400 shadow-sm shadow-blue-100"
                        : "border border-dashed border-slate-300 hover:border-blue-300"
                    }`}
                  >
                    {/* Card header bar */}
                    <div className={`flex items-center justify-between px-4 py-2.5 ${
                      useJanContext ? "bg-blue-500" : "bg-slate-100"
                    }`}>
                      <div className="flex items-center gap-2">
                        <CalendarClock className={`w-3.5 h-3.5 flex-shrink-0 ${useJanContext ? "text-white" : "text-slate-500"}`} />
                        <span className={`text-xs font-extrabold uppercase tracking-widest ${useJanContext ? "text-white" : "text-slate-500"}`}>
                          January / Spring Enrollment
                        </span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          useJanContext ? "bg-white/25 text-white" : badgeStyle
                        }`}>
                          {badge}
                        </span>
                      </div>
                      {/* ON/OFF pill */}
                      <div className={`flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-widest ${useJanContext ? "text-white" : "text-slate-400"}`}>
                        <div className={`relative w-7 h-4 rounded-full transition-all duration-200 ${useJanContext ? "bg-white/40" : "bg-slate-300"}`}>
                          <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-all duration-200 ${useJanContext ? "left-3.5" : "left-0.5"}`} />
                        </div>
                        {useJanContext ? "ON" : "OFF"}
                      </div>
                    </div>
                    {/* Card body */}
                    <div className={`px-4 py-3 flex items-start gap-3 ${useJanContext ? "bg-blue-50" : "bg-white"}`}>
                      <Sparkles className={`w-4 h-4 flex-shrink-0 mt-0.5 ${useJanContext ? "text-blue-500" : "text-slate-300"}`} />
                      <p className={`text-xs leading-relaxed ${useJanContext ? "text-blue-800" : "text-slate-500"}`}>
                        {useJanContext
                          ? <><strong>Active —</strong> AI will frame this email as a mid-year intake opportunity, emphasising spring semester availability and January start dates.</>
                          : <>Tap to apply <strong>spring enrollment framing</strong> — best for mid-year or late-cycle candidates targeting January intake.</>
                        }
                      </p>
                    </div>
                  </button>
                </div>
              );
            })()}

            {/* Message type */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Message Type</label>
              <div className="flex gap-2 flex-wrap">
                {messageTypes.map(t => (
                  <button
                    key={t.value}
                    data-testid={`message-type-${t.value}`}
                    onClick={() => setMessageType(t.value)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${messageType === t.value ? "bg-orange-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                  >
                    {t.label}
                  </button>
                ))}

                {/* ── Late Enquiry (Post-Start) ──────────────────────────────
                    Conditionally rendered: JUCO college + Sept 1 – Nov 30 window.
                    Amber-styled to signal it's time-limited.                  */}
                {selectedCollege && getDivisionMeta(selectedCollege).isJUCO && (() => {
                  const { inWindow, isUrgent, daysLeft } = getLateEnquiryMeta();
                  if (!inWindow) return null;
                  const isSelected = messageType === "late_enquiry";
                  return (
                    <div key="late_enquiry" className="flex flex-col items-start gap-0.5">
                      <button
                        data-testid="message-type-late_enquiry"
                        onClick={() => setMessageType("late_enquiry")}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                          isUrgent
                            ? isSelected
                              ? "bg-amber-600 text-white border-2 border-amber-700"
                              : "bg-amber-100 text-amber-900 border-2 border-amber-500 hover:bg-amber-200"
                            : isSelected
                              ? "bg-amber-500 text-white border border-amber-600"
                              : "bg-amber-50 text-amber-800 border border-amber-300 hover:bg-amber-100"
                        }`}
                      >
                        Late Enquiry (Post-Start)
                      </button>
                      <span className={`text-xs ml-0.5 ${isUrgent ? "font-bold text-amber-700" : "text-amber-500"}`}>
                        {isUrgent
                          ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} left`
                          : "Available until Nov 30"}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Position */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Primary Position</label>
              <div className="relative">
                <select
                  data-testid="compose-position-select"
                  value={position}
                  onChange={e => setPosition(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 outline-none appearance-none bg-white pr-8 text-slate-900"
                >
                  <option value="point guard">Point Guard</option>
                  <option value="shooting guard">Shooting Guard</option>
                  <option value="combo guard">Combo Guard</option>
                  <option value="small forward">Small Forward</option>
                  <option value="power forward">Power Forward</option>
                  <option value="center">Center</option>
                </select>
                <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Secondary Position <span className="text-slate-400 normal-case font-normal">(optional — adds versatility to your email)</span></label>
              <div className="relative">
                <select
                  data-testid="compose-secondary-position-select"
                  value={secondaryPosition}
                  onChange={e => setSecondaryPosition(e.target.value)}
                  className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 outline-none appearance-none bg-white pr-8 text-slate-900"
                >
                  <option value="">None</option>
                  <option value="point guard">Point Guard</option>
                  <option value="shooting guard">Shooting Guard</option>
                  <option value="combo guard">Combo Guard</option>
                  <option value="small forward">Small Forward</option>
                  <option value="power forward">Power Forward</option>
                  <option value="center">Center</option>
                </select>
                <ChevronDown className="absolute right-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Stats */}
            <div className="mb-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Stats (auto-filled from your profile)</label>
              <textarea
                data-testid="compose-stats-input"
                value={stats}
                onChange={e => setStats(e.target.value)}
                rows={4}
                placeholder={"School: 18.5 PPG, 6.2 APG\nClub: 22.0 PPG, 8.0 APG\nNational: 15.0 PPG, 4.5 APG"}
                className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-none font-mono"
              />
            </div>

            {/* College reply context indicator */}
            {collegeContext && !["initial_outreach","follow_up"].includes(messageType) && (
              collegeContext.latest_reply || collegeContext.reply_outcome ? (
                <div data-testid="reply-context-indicator" className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-start gap-2.5">
                  <MessageSquare className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-blue-700">Reply context loaded</p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      {collegeContext.latest_reply
                        ? `AI will respond inline with: "${collegeContext.latest_reply.subject || "their reply"}"`
                        : `Coach outcome: ${collegeContext.reply_outcome?.replace(/_/g, " ")}`}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mb-4 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 flex items-start gap-2.5">
                  <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-500">No reply on record for this college — AI will draft a general {messageType.replace(/_/g," ")} email.</p>
                </div>
              )
            )}

            {/* Stats preview card */}
            {(() => {
              const contexts = [
                { label: "School",   color: "bg-orange-500", keys: ["college_ppg","college_apg","college_rpg","college_spg","college_fg_percent","college_three_pt_percent"] },
                { label: "Club",     color: "bg-blue-500",   keys: ["academy_ppg","academy_apg","academy_rpg","academy_spg","academy_fg_percent","academy_three_pt_percent"] },
                { label: "National", color: "bg-green-600",  keys: ["country_ppg","country_apg","country_rpg","country_spg","country_fg_percent","country_three_pt_percent"] },
              ];
              const statLabels = ["PPG","APG","RPG","SPG","FG%","3PT%"];
              const rows = contexts.map(ctx => ({
                ...ctx,
                parts: ctx.keys.map((k, i) => playerProfile[k] ? { lbl: statLabels[i], val: playerProfile[k] } : null).filter(Boolean)
              })).filter(r => r.parts.length > 0);
              if (rows.length === 0) return null;
              return (
                <div data-testid="stats-preview-card" className="mb-4 border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 flex items-center gap-1.5">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Stats going into this email</span>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {rows.map(row => (
                      <div key={row.label} className="flex items-center gap-3 px-3 py-2">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${row.color}`} />
                        <span className="text-xs font-bold text-slate-600 w-24 flex-shrink-0">{row.label}</span>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
                          {row.parts.map(({ lbl, val }) => (
                            <span key={lbl} className="text-xs text-slate-700">
                              <span className="font-bold">{val}</span>
                              <span className="text-slate-400 ml-0.5">{lbl}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

            <button
              data-testid="compose-generate-btn"
              onClick={generateDraft}
              disabled={generating || !selectedCollege || !profileLoaded || !selectedCoach}
              className="w-full bg-orange-500 text-white font-bold uppercase tracking-wider rounded-lg py-3 text-sm hover:bg-orange-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Wand2 className="w-4 h-4" />
              {generating ? "Generating..." : !profileLoaded ? "Loading your profile..." : !selectedCoach ? "Select a coach to draft" : "Generate Draft with AI"}
            </button>
            {/* Inline coach-gate message — shown when college selected but no coach picked */}
            {selectedCollege && !selectedCoach && (
              <p className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-1.5" data-testid="coach-required-hint">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-600" />
                Choose a coach from the dropdown above — a draft without a named recipient is not ready to send.
              </p>
            )}

            {/* Tape indicator */}
            {playerProfile.highlight_tape_url ? (
              <div className="mt-2 flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5" data-testid="tape-included-indicator">
                <Film className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-emerald-700">Highlight tape auto-included in draft</p>
                  <a href={playerProfile.highlight_tape_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-600 hover:underline truncate block max-w-xs">
                    {playerProfile.highlight_tape_url}
                  </a>
                </div>
              </div>
            ) : (
              <p className="mt-2 text-xs text-slate-400 flex items-center gap-1.5" data-testid="tape-missing-hint">
                <Film className="w-3 h-3" />
                Add your Hudl/YouTube link in Profile to auto-include in drafts
              </p>
            )}
          </div>
        </div>

        {/* Right: Draft Editor */}
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h2 className="font-bold text-slate-900" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>Email Draft</h2>
              {/* Explicit confirmation tag — only shown when spring framing was actually applied to this draft */}
              {useJanContext && draft && (
                <span
                  data-testid="jan-context-applied-tag"
                  className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-700 text-[10px] font-extrabold px-2.5 py-1 rounded-full border border-blue-200 uppercase tracking-wider"
                >
                  <CalendarClock className="w-3 h-3" />
                  Spring framing applied
                </span>
              )}
            </div>
            {draft && (
              <div className="flex gap-2 flex-wrap">
                <button data-testid="compose-copy-btn" onClick={copyDraft} className="flex items-center gap-1.5 text-xs bg-slate-100 text-slate-700 hover:bg-slate-200 px-3 py-1.5 rounded-lg font-medium transition-colors">
                  <Copy className="w-3.5 h-3.5" /> {copied ? "Copied!" : "Copy"}
                </button>
                <button
                  data-testid="save-template-btn"
                  onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                  className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${showSaveTemplate ? "bg-purple-100 text-purple-700" : "bg-purple-50 text-purple-600 hover:bg-purple-100"}`}
                >
                  <BookMarked className="w-3.5 h-3.5" />
                  {templateSaved ? "Template Saved!" : "Save as Template"}
                </button>
              </div>
            )}
          </div>

          {/* Optional: Save as Template inline form */}
          {showSaveTemplate && draft && (
            <form
              data-testid="save-template-form"
              onSubmit={handleSaveTemplate}
              className="mb-4 bg-purple-50 border border-purple-200 rounded-lg p-3"
            >
              <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">Save as Template</p>
              <div className="flex gap-2">
                <input
                  data-testid="template-name-input"
                  type="text"
                  required
                  value={templateName}
                  onChange={e => setTemplateName(e.target.value)}
                  placeholder="Template name (e.g. D1 Initial Outreach)..."
                  className="flex-1 border border-purple-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 outline-none bg-white"
                />
                <button
                  type="submit"
                  disabled={savingTemplate || !templateName.trim()}
                  className="bg-purple-600 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-60"
                >
                  {savingTemplate ? "Saving..." : "Save"}
                </button>
                <button type="button" onClick={() => setShowSaveTemplate(false)} className="text-slate-400 hover:text-slate-600 p-2">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {/* Subject */}
          <div className="mb-3">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Subject</label>
            <input
              data-testid="compose-subject-input"
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Email subject line..."
              className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
            />
          </div>

          {/* Body */}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide block mb-1.5">Body</label>
            <textarea
              data-testid="compose-body-textarea"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={16}
              placeholder={generating ? "AI is drafting your email..." : "Your AI-generated draft will appear here. You can edit it freely."}
              className="w-full border border-slate-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-none"
            />
          </div>

          {/* Open in Gmail — primary send action */}
          {draft && (
            <div className="mt-4 space-y-3">
              {/* Send-guard: hard-stop banner shown before the button when placeholder tokens detected */}
              {sendError && (
                <div
                  data-testid="send-guard-error"
                  className="flex items-start gap-2.5 bg-red-50 border border-red-400 rounded-lg px-4 py-3"
                  role="alert"
                >
                  <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-red-800">Send blocked — placeholder detected</p>
                    <p className="text-xs text-red-700 mt-0.5 leading-relaxed">{sendError}</p>
                  </div>
                </div>
              )}
              {/* Email gate: derive once, use throughout this block */}
              {(() => {
                const coachEmail = (selectedCoach?.email || "").trim();
                const hasEmail = !!coachEmail;
                const hasPlayerEmail = !!(playerProfile.email && playerProfile.email.trim());
                // Only when the coach's email exists: branch on the PLAYER's own
                // email domain. No player email → leave today's Gmail button +
                // amber warning untouched. Gmail/Google-Workspace domain → keep
                // today's Gmail button + "Will open as..." note. Any other
                // domain → mailto: link + copy-draft fallback instead.
                const useMailtoFallback = hasEmail && hasPlayerEmail && !isPlayerEmailGmail();
                const mailtoUrl = hasEmail
                  ? `mailto:${coachEmail}?subject=${encodeURIComponent(subject || "Basketball Scholarship Inquiry")}&body=${encodeURIComponent(draft)}`
                  : "";
                return (
                  <>
                    {/* Step 1 — Open Gmail, or the mailto fallback for non-Gmail players */}
                    {hasEmail && useMailtoFallback ? (
                      <div className="space-y-2">
                        <a
                          href={mailtoUrl}
                          data-testid="mailto-fallback-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            openMailtoFallback();
                          }}
                          className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-700 text-white font-bold uppercase tracking-wider rounded-lg py-3.5 text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <Mail className="w-4 h-4" />
                          Open in Mail App
                          <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                        </a>
                        <button
                          type="button"
                          data-testid="copy-draft-btn"
                          onClick={copyDraftForMailto}
                          className="w-full flex items-center justify-center gap-1.5 text-xs bg-slate-100 text-slate-700 hover:bg-slate-200 px-3 py-2 rounded-lg font-medium transition-colors"
                        >
                          <Copy className="w-3.5 h-3.5" /> {mailtoDraftCopied ? "Copied!" : "Copy draft to clipboard"}
                        </button>
                      </div>
                    ) : hasEmail ? (
                      <button
                        data-testid="open-in-gmail-btn"
                        onClick={openInGmail}
                        disabled={!selectedCollege}
                        className="w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-700 text-white font-bold uppercase tracking-wider rounded-lg py-3.5 text-sm transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <Mail className="w-4 h-4" />
                        Open in Gmail
                        <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                      </button>
                    ) : (
                      <div
                        data-testid="no-email-fallback"
                        className="w-full flex items-center justify-center gap-2 bg-slate-100 text-slate-400 font-bold uppercase tracking-wider rounded-lg py-3.5 text-sm cursor-not-allowed border border-slate-200 select-none"
                        title="No verified email address on file for this coach"
                      >
                        <Mail className="w-4 h-4" />
                        Open in Gmail
                        <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                      </div>
                    )}

                    {/* Email missing — show actionable fallback */}
                    {!hasEmail && selectedCoach && (
                      <div
                        data-testid="no-email-notice"
                        className="flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3"
                      >
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-amber-800">
                            No verified email for {selectedCoach.name}
                          </p>
                          <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                            Contact via the athletics department website, or use the <strong>Report</strong> button on the college page to flag this coach's details.
                          </p>
                        </div>
                      </div>
                    )}

                    {useMailtoFallback ? (
                      <p className="text-xs text-slate-500 text-center" data-testid="mailto-fallback-note">
                        Opens your device's default mail app — double-check which account it sends from before hitting send.
                      </p>
                    ) : playerProfile.email && hasEmail ? (
                      <p className="text-xs text-slate-500 text-center">
                        Will open as <span className="font-semibold text-slate-700">{playerProfile.email}</span> — make sure you're signed into this account in Gmail.
                      </p>
                    ) : !playerProfile.email && hasEmail ? (
                      <p className="text-xs text-amber-600 text-center">
                        No email set in your <a href="/profile" className="underline font-semibold">Profile</a> — Gmail may open the wrong account. Add your recruiting email to fix this.
                      </p>
                    ) : null}

                    {/* Step 2 — Awaiting confirmation */}
                    {awaitingConfirmation && (
                      <div className="border border-orange-200 bg-orange-50 rounded-xl p-4 space-y-3" data-testid="gmail-confirm-card">
                        <div className="flex items-start gap-2.5">
                          <Mail className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-bold text-orange-900">Please confirm once you've sent the email</p>
                            <p className="text-xs text-orange-700 mt-1 leading-relaxed">
                              CourtBound can't detect sends from Gmail, so your confirmation lets us log this to your <strong>Sent Emails</strong> history and set a <strong>7-day follow-up reminder</strong> for {selectedCollege?.name} in Priority Actions.
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            data-testid="confirm-email-sent-btn"
                            onClick={confirmEmailSent}
                            disabled={confirming}
                            className="flex-1 flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm uppercase tracking-wider rounded-lg py-2.5 transition-colors disabled:opacity-60"
                          >
                            <CheckCircle className="w-4 h-4" />
                            {confirming ? "Logging..." : "Yes, Email Sent"}
                          </button>
                          <button
                            data-testid="cancel-gmail-confirm-btn"
                            onClick={() => setAwaitingConfirmation(false)}
                            className="px-4 py-2.5 rounded-lg border border-orange-200 text-sm font-medium text-orange-600 hover:bg-orange-100 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Step 3 — Confirmed success */}
                    {confirmed && (
                      <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3" data-testid="gmail-sent-confirmation">
                        <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-bold text-emerald-800">Email logged successfully</p>
                          <p className="text-xs text-emerald-600 mt-0.5">
                            <strong>{selectedCollege?.name}</strong> marked as Contacted. Follow-up reminder set for 7 days — check Priority Actions on your Dashboard.
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {!draft && !generating && (
            <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
              <p className="text-sm text-orange-700 font-medium">
                Select a college and click "Generate Draft with AI" to get a personalised email draft.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
